import React from 'react';
import { View, Text, StyleSheet, Linking, TouchableOpacity } from 'react-native';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { GlassCard } from '@/components/shared/redesign';
import { IMaintenancePriority } from '@/store/api/maintenanceApi';
import {
  SEVERITY_CHOICES,
  resolveSeverityPriority,
  choiceIdForPriorityName,
  SeverityChoiceId,
} from '@/utils/residents/maintenance/maintenanceSeverity';

interface StepUrgencyProps {
  severity: string; // stored priority name
  onSeverityChange: (priorityName: string) => void;
  priorities: IMaintenancePriority[];
  maintenancePhone?: string;
}

const Dot: React.FC<{ selected: boolean; color: string; border: string }> = ({ selected, color, border }) => (
  <View style={[styles.radio, { borderColor: selected ? color : border }]}>
    {selected ? <View style={[styles.radioInner, { backgroundColor: color }]} /> : null}
  </View>
);

export const StepUrgency: React.FC<StepUrgencyProps> = ({
  severity,
  onSeverityChange,
  priorities,
  maintenancePhone,
}) => {
  const { colors, semantic } = useThemeColors();
  const selectedId = choiceIdForPriorityName(severity, priorities);

  const handleSelect = (id: SeverityChoiceId) => {
    const priority = resolveSeverityPriority(id, priorities);
    if (priority) onSeverityChange(priority.name);
  };

  return (
    <View style={styles.container}>
      <Text style={[styles.heading, { color: colors.text.primary }]}>How does this affect you?</Text>
      {SEVERITY_CHOICES.map((choice) => {
        const selected = selectedId === choice.id;
        return (
          <GlassCard
            key={choice.id}
            padding={16}
            interactive
            onPress={() => handleSelect(choice.id)}
            style={styles.row}
          >
            <Text style={[styles.label, { color: colors.text.primary }]}>{choice.label}</Text>
            <Dot selected={selected} color={semantic.interactive.primary} border={colors.card.borderSolid} />
            {choice.urgent && selected && maintenancePhone ? (
              <TouchableOpacity onPress={() => Linking.openURL(`tel:${maintenancePhone}`)} style={styles.urgent}>
                <Text style={[styles.urgentText, { color: colors.accent.red }]}>
                  For emergencies, call the maintenance team
                </Text>
              </TouchableOpacity>
            ) : null}
          </GlassCard>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { paddingHorizontal: REDESIGN_SPACING.horizontalPadding, gap: REDESIGN_SPACING.sectionGap },
  heading: { ...REDESIGN_TYPOGRAPHY.heading1, marginBottom: 4 },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  label: { ...REDESIGN_TYPOGRAPHY.body, flex: 1 },
  radio: { width: 20, height: 20, borderRadius: 10, borderWidth: 2, alignItems: 'center', justifyContent: 'center' },
  radioInner: { width: 10, height: 10, borderRadius: 5 },
  urgent: { width: '100%', marginTop: 4 },
  urgentText: { ...REDESIGN_TYPOGRAPHY.caption },
});

export default StepUrgency;
